/**
 * 需求单状态（DemandStateEnum）→ Tag 颜色 映射。
 *
 * 设计稿出处：Figma【最新】【常规采购】投放类-1级单预算管理线上化
 *   节点 31175:63841（"需要处理节点-黄色 / 无需处理节点-蓝色 / 结束-灰色"）
 *
 * 后端契约（参见 `BudgetDemandItemVo`）：
 *   - `plan_demand_state` / `demand_state`            —— 已是中文文本，直接显示
 *   - `plan_demand_state_enum` / `demand_state_enum`  —— DemandStateEnum 枚举值，
 *                                                       前端据此控制 Tag 颜色
 *
 * 三类语义：
 *   - `warning`（黄色 / light-orange）：本端 / 业务方需要处理的节点（草稿、新建、各类审批与验收）
 *   - `info`   （蓝色 / light-blue）  ：流程进行中、无需本端处理（OA 审批、制作中、绑定数据、进行中等）
 *   - `done`   （灰色 / 默认无色）    ：已结束（关闭 / 废弃 / 已完成）
 *
 * 未匹配到的状态默认按 `info`（light-blue）兜底，与改造前 OrderNumberCell 的旧行为保持一致，
 * 避免后端新增枚举时前端无 Tag 显示。
 */

/** Tag 语义颜色 —— 与 @bedrock/components Tag color 对齐 */
export type DemandStateTagColor = 'light-orange' | 'light-blue' | 'light-grey';

/**
 * 需要处理节点（黄色）：本端用户 / 业务方需手动操作的节点。
 *
 * 包含：草稿、新建、需求前置审批、各类审批（商务/策划/需求方/负责人）、
 * 各类验收（策划复核/商务验收/需求方验收/QC 验收/营销结算/付款）、
 * 待报价节点（PENDING_QUOTE 在不同口径下既可视作"待供应商响应"也可视作
 * "本端跟进"，业务上口径偏黄；如需细分可在后续 spec 中调整）。
 */
const NEEDS_ACTION_STATES = new Set<string>([
  'DRAFT',                          // 草稿（plan_demand 草稿态特殊值）
  'NEWLY_BUILT',                    // 新建
  'PRE_APPROVE',                    // 需求前置审批
  'PENDING_DESIGN',                 // 待策划设计 / 策划设计-草稿
  'PENDING_BIDDING',                // 比选中
  'PENDING_ASSESS',                 // 商务评估 / 待评估
  'PENDING_QUOTE',                  // 待报价
  'QUOTE_SCORE',                    // 询比价综合评分
  'BUSINESS_AUDIT_QUOTE',           // 报价待商务确认
  'BUSINESS_LEADER_AUDIT_QUOTE',    // 品类商务负责人确认报价
  'PLANNER_AUDIT_QUOTE',            // 报价待策划确认
  'PLANNER_LEADER_AUDIT_QUOTE',     // 策划负责人确认
  'MARKETING_AUDIT_QUOTE',          // 报价待需求方确认
  'MARKETING_LEADER_AUDIT_QUOTE',   // 报价待需求方负责人确认
  'PLANNER_ACCEPT',                 // 策划复核
  'PLANNER_LEADER_ACCEPT',          // 策划负责人复核
  'BUSINESS_ACCEPT',                // 商务验收
  'DEMANDER_ACCEPT',                // 需求方验收
  'APPROVE_SETTLE',                 // 需求方负责人验收
  'QC_ACCEPT',                      // QC 验收复核
  'SUBMIT_OA',                      // 营销业务验收与结算
  'SUBMIT_REIMBURSE',               // 提交付款
]);

/**
 * 无需处理节点（蓝色）：流程在系统 / 外部流转中，本端无需立即处理。
 *
 * 对应设计稿示例：「进行中」「供应商报价」「OA 定标审批」（FEE_APPROVE）。
 * 制作中 / 绑定数据节点同属流程中性进行态。
 */
const IN_PROGRESS_STATES = new Set<string>([
  'FEE_APPROVE',                    // OA 定标审批
  'MAKING',                         // 制作中
  'BIND_EXECUTION_DATA',            // 绑定数据节点
  'UNDERWAY',                       // 进行中
]);

/**
 * 结束节点（灰色）：关闭 / 废弃 / 已完成（业务上 SUBMIT_REIMBURSE 之后视为结束）。
 *
 * 注：DemandStateEnum 中并无单独的 "已完成" 枚举（设计稿示例的"已完成"对应
 * 业务上的最终结束态），目前后端落到 CLOSED / ABANDON 两个值。
 */
const FINISHED_STATES = new Set<string>([
  'CLOSED',                         // 关闭
  'ABANDON',                        // 废弃
]);

/**
 * 根据 DemandStateEnum 枚举值返回对应的 Tag 颜色。
 *
 * @param stateEnum - `plan_demand_state_enum` / `demand_state_enum` 字段值，
 *                    大小写不敏感（后端历史数据存在小写场景，统一 toUpperCase）。
 * @returns Tag 颜色：light-orange / light-blue / default
 */
export function getDemandStateTagColor(
  stateEnum?: string | null,
): DemandStateTagColor {
  if (!stateEnum) return 'light-blue';
  const key = String(stateEnum).toUpperCase();

  if (NEEDS_ACTION_STATES.has(key)) return 'light-orange';
  if (FINISHED_STATES.has(key)) return 'light-grey';
  if (IN_PROGRESS_STATES.has(key)) return 'light-blue';

  // 未识别枚举值：按 light-blue 兜底（保持改造前默认行为）
  return 'light-blue';
}
